import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { cn } from '@/lib/cn';
import type { HotelOption } from '@/lib/allocation/types';
import { useRequestOverride, useSearchHotels } from './use-allocation';
import { isPriced } from '@/lib/allocation/price';
import { Unpriced } from '@/components/allocation/unpriced';

// c7 — manual hotel override (TDS §8). Force-assign a specific hotel to the
// selected PNR group outside the auto-allocation ranking, with a justification.
// Posts to POST /v1/allocation/override (a supervisor approves downstream).

interface OverrideDialogProps {
  open: boolean;
  onClose: () => void;
  caseUrn: string;
  subCaseUrn: string | undefined;
  subCaseLabel: string | undefined;
  airportCode: string;
  rooms: number;
}

export function OverrideDialog({
  open,
  onClose,
  caseUrn,
  subCaseUrn,
  subCaseLabel,
  airportCode,
  rooms,
}: OverrideDialogProps) {
  const { t } = useTranslation('caseWork');
  const search = useSearchHotels(caseUrn, airportCode, undefined, subCaseUrn);
  const override = useRequestOverride();
  const [picked, setPicked] = useState<HotelOption | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setPicked(null);
      setReason('');
      search.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('allocation.override.title')}</DialogTitle>
          <DialogDescription>
            {t('allocation.override.description', { group: subCaseLabel ?? '—' })}
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3 text-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!subCaseUrn || !picked) return;
            await override.mutateAsync({
              caseUrn,
              subCaseUrn,
              hotelUrn: picked.hotelUrn,
              offerUrn: picked.offerUrn,
              rooms,
              reason,
            });
            onClose();
          }}
        >
          <div>
            <span className="mb-1 block text-xs uppercase text-muted-foreground">
              {t('allocation.override.pickHotel')}
            </span>
            {search.isError ? (
              <p role="alert" className="text-destructive">{t('assignment.searchFailed')}</p>
            ) : search.isPending || !search.data ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : search.data.length === 0 ? (
              <p className="text-muted-foreground">{t('allocation.override.noHotels')}</p>
            ) : (
              <ul className="max-h-64 space-y-1 overflow-auto">
                {[...search.data].sort((a, b) => a.distanceKm - b.distanceKm).map((hotel) => (
                  <li key={hotel.offerUrn}>
                    <button
                      type="button"
                      onClick={() => setPicked(hotel)}
                      className={cn(
                        'flex w-full items-center justify-between rounded border p-2 text-left',
                        picked?.offerUrn === hotel.offerUrn
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:bg-muted/50',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2 font-medium">
                        {hotel.name}
                        <span className="flex items-center text-amber-500">
                          {Array.from({ length: hotel.stars }).map((_, i) => (
                            <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                          ))}
                        </span>
                        <HotelVendorBadge urn={hotel.hotelUrn} />
                      </span>
                      <span className="flex shrink-0 items-baseline gap-2">
                        <span className="text-xs text-muted-foreground">
                          {t('allocation.override.hotelMeta', {
                            km: hotel.distanceKm.toFixed(1),
                          })}
                        </span>
                        {isPriced(hotel.ratePerRoomUSD) ? (
                          <span className="text-sm font-semibold tabular-nums">
                            ${hotel.ratePerRoomUSD}
                            <span className="text-[11px] font-normal text-muted-foreground">
                              {t('common:holds.perNight')}
                            </span>
                          </span>
                        ) : (
                          <Unpriced className="text-xs" />
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('allocation.override.justification')}
            </span>
            <textarea
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              placeholder={t('allocation.override.justificationPlaceholder')}
            />
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:common.cancel')}
            </Button>
            <Button type="submit" disabled={override.isPending || !picked || !reason || !subCaseUrn}>
              {override.isPending ? t('shared.submitting') : t('allocation.override.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
